/**
 * Web-side API client for the devices-management page (/auth/devices).
 *
 * Reuses the user's existing Firebase Auth session: every call sends
 * the current ``getIdToken()`` as a Bearer
 * (the device list is per-Caller; account scope is irrelevant for it,
 * but the backend's ``FirebaseIdTokenAuthentication`` still requires
 * the header).
 *
 * The Vue page (``AuthDevices.vue``) is HITL; this kernel is the
 * Jest-testable piece.
 */

export interface ConnectedDevice {
	id: string;
	uid: string;
	plugin_kind: string | null;
	user_agent_hint?: string | null;
	connected_at?: string;
	last_used_at?: string;
	revoked_at?: string | null;
	// A RunDiffusion developer's feedback agent (restServer apiv2/agent/): 'feedback_agent', with
	// the computer it runs on
	client_kind?: string | null;
	host?: string | null;
}


export type ListDevicesResult =
	| { ok: true; devices: ConnectedDevice[] }
	| { ok: false; reason: 'network' | 'failed'; message?: string };

export type RevokeResult =
	| { ok: true }
	| { ok: false; reason: 'not_found' | 'forbidden' | 'network' | 'failed'; message?: string };


export interface AuthedRequestInput {
	baseUrl: string;
	idToken: string;
}


function authedHeaders(idToken: string): Record<string, string> {
	return {
		'Content-Type': 'application/json',
		'Authorization': `Bearer ${idToken}`,
	};
}


export async function listConnectedDevices(input: AuthedRequestInput): Promise<ListDevicesResult> {
	const url = `${input.baseUrl.replace(/\/$/, '')}/auth/devices`;
	let resp: Response;
	try {
		resp = await fetch(url, { method: 'GET', headers: authedHeaders(input.idToken) });
	} catch {
		return { ok: false, reason: 'network' };
	}
	if (!resp.ok) {
		return { ok: false, reason: 'failed' };
	}
	const body = await resp.json();
	return { ok: true, devices: body.devices || [] };
}


export async function revokeDevice(
	input: AuthedRequestInput & { deviceId: string },
): Promise<RevokeResult> {
	const url = `${input.baseUrl.replace(/\/$/, '')}/auth/devices/${encodeURIComponent(input.deviceId)}/revoke`;
	let resp: Response;
	try {
		resp = await fetch(url, { method: 'POST', headers: authedHeaders(input.idToken) });
	} catch {
		return { ok: false, reason: 'network' };
	}
	if (resp.status === 204) {
		return { ok: true };
	}
	if (resp.status === 404) {
		return { ok: false, reason: 'not_found' };
	}
	if (resp.status === 403) {
		return { ok: false, reason: 'forbidden' };
	}
	return { ok: false, reason: 'failed' };
}


export async function signOutEverywhere(input: AuthedRequestInput): Promise<RevokeResult> {
	const url = `${input.baseUrl.replace(/\/$/, '')}/auth/sign-out-everywhere`;
	let resp: Response;
	try {
		resp = await fetch(url, { method: 'POST', headers: authedHeaders(input.idToken) });
	} catch {
		return { ok: false, reason: 'network' };
	}
	if (resp.status === 204) {
		return { ok: true };
	}
	return { ok: false, reason: 'failed' };
}
